import { FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import { IConditionsSideblock } from "./ConditionsSideblock.props";
import { useAppSelector } from "@/store/hooks";
import { selectDataList } from "@/store/ses/sesDataElemSlice";
import cn from "classnames";
import styles from "./ConditionsSideblock.module.scss";
import { AtomicCondition, ConditionVariantType } from "@/types/ses/sesItemConfigTypes";
import { CONDITION_CONFIRMATION_LIST_VARIANTS, ROBOT_EVENT_LIST } from "@/constants/robotConfigLists";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CONDITIONS_LIST_ICONS, OPERATION_CONDITIONS_ICONS } from "@/components/ConditionBlock/Condition/Condition.const";
import { Divider } from "@mui/material";
import { useTranslate } from "@/hooks";
import { faThumbsDown } from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

const ConditionsSideblock: FC<IConditionsSideblock> = ({ conditions, ...props }) => {
	const dataElemList = useAppSelector(selectDataList); // список ЭД
	const namesDataElemsById = useMemo(() => dataElemList.data.reduce<Record<string, string>>((acc, el) => ({ ...acc, [el.id]: el.name }), {}), [dataElemList.data]);
	const translate = useTranslate();
	const containerForOrConditions = useRef<HTMLDivElement>(null);
	const [containerForOrConditionsWidth, setContainerForOrConditionsWidth] = useState<number>(
		containerForOrConditions.current !== null ? containerForOrConditions.current.offsetWidth : 0
	);
	const widestElement =
		conditions.reduce<number>((acc, el, i) => {
			el.forEach((item) => {
				let sumWidth = 0;
				switch (item.type) {
					case "data":
						sumWidth = item.id.length + item.value.length;
						break;
					case "variable":
						sumWidth = item.id.length + item.value.length;
						break;
					case "confirmation":
						sumWidth = item.value.length;
						break;
					case "interval":
						sumWidth = item.id.length;
						break;
					case "break":
						// добавить рендер для break если понадобиться
						break;
					default:
						break;
				}

				if ((acc + 60) * i + sumWidth > containerForOrConditionsWidth) return;

				if (acc < sumWidth) acc = sumWidth;
			});
			return acc;
		}, 0) + 67;

	const conditionInContainer = Math.floor(containerForOrConditionsWidth / widestElement);

	useLayoutEffect(() => {
		const updateWidth = () => {
			if (containerForOrConditions.current) {
				setContainerForOrConditionsWidth(containerForOrConditions.current.offsetWidth);
			}
		};

		updateWidth();
		window.addEventListener("resize", updateWidth);

		return () => window.removeEventListener("resize", updateWidth);
	}, [containerForOrConditions.current]);

	// Функция для получения иконки
	const getConditionIcon = (type: Exclude<ConditionVariantType["type"], "or">) => {
		const icon = CONDITIONS_LIST_ICONS[type];
		if (typeof icon === "string") {
			// Если это строка, возвращаем span
			return <span style={{ fontSize: "10px", minWidth: "11px" }}>{icon}</span>;
		}
		// Если это IconDefinition, возвращаем FontAwesomeIcon
		return <FontAwesomeIcon icon={icon as IconDefinition} size="lg" />;
	};
	const renderAtomicConditions = (conditionAnd: AtomicCondition, lastElem: boolean, elementsRemained?: number) => {
		switch (conditionAnd.type) {
			case "data": {
				return (
					<div key={JSON.stringify(conditionAnd)} className={cn(styles["conditionsSideblock_conditionAnd"])}>
						<span className={cn(styles["conditionsSideblock_conditionAnd-icon"])}>{getConditionIcon(conditionAnd.type)}</span>

						{namesDataElemsById[conditionAnd.id] ? (
							<span
								className={cn(styles["conditionsSideblock_conditionAnd-text"])}
								title={namesDataElemsById[conditionAnd.id] ? namesDataElemsById[conditionAnd.id] : ""}
							>
								{namesDataElemsById[conditionAnd.id]}
							</span>
						) : (
							<></>
						)}
						{conditionAnd.value ? (
							<span className={cn(styles["conditionsSideblock_conditionAnd-text"])} title={conditionAnd.value ? conditionAnd.value : ""}>
								{conditionAnd.value}
							</span>
						) : (
							<></>
						)}
						{lastElem && (
							<span
								className={cn(styles["conditionsSideblock_conditionAnd-text"])}
								style={{ minWidth: "30px" }}
								title={elementsRemained ? `...(+${elementsRemained})` : ""}
							>
								{elementsRemained ? `...(+${elementsRemained})` : ""}
							</span>
						)}
					</div>
				);
			}
			case "variable":
				return (
					<div key={JSON.stringify(conditionAnd)} className={cn(styles["conditionsSideblock_conditionAnd"])}>
						<span className={cn(styles["conditionsSideblock_conditionAnd-icon"])} style={{ fontSize: "10px", minWidth: "11px" }}>
							{`{ }`}
						</span>
						{conditionAnd.id ? (
							<span className={cn(styles["conditionsSideblock_conditionAnd-text"])} title={conditionAnd.id ?? ""}>
								{conditionAnd.id}
							</span>
						) : (
							<></>
						)}
						{conditionAnd.operation ? (
							<span className={cn(styles["conditionsSideblock_conditionAnd-icon"])} title={conditionAnd.value}>
								<FontAwesomeIcon icon={OPERATION_CONDITIONS_ICONS[conditionAnd.operation]} />
							</span>
						) : (
							<></>
						)}
						{conditionAnd.value ? (
							<span className={cn(styles["conditionsSideblock_conditionAnd-text"])} title={conditionAnd.value}>
								{conditionAnd.value}
							</span>
						) : (
							<></>
						)}

						{lastElem && (
							<span
								className={cn(styles["conditionsSideblock_conditionAnd-text"])}
								style={{ minWidth: "30px" }}
								title={elementsRemained ? `...(+${elementsRemained})` : ""}
							>
								{elementsRemained ? `...(+${elementsRemained})` : ""}
							</span>
						)}
					</div>
				);
			case "confirmation":
				return (
					<div key={JSON.stringify(conditionAnd)} className={cn(styles["conditionsSideblock_conditionAnd"])}>
						<span className={cn(styles["conditionsSideblock_conditionAnd-icon"])} style={{ marginRight: "4px" }}>
							{CONDITION_CONFIRMATION_LIST_VARIANTS.find((el) => el.value === conditionAnd.value)?.translation === "title_confirmation" ? (
								<FontAwesomeIcon icon={CONDITIONS_LIST_ICONS.confirmation as IconDefinition} size="lg" />
							) : (
								<FontAwesomeIcon icon={faThumbsDown} size="lg" />
							)}
						</span>
						<span className={cn(styles["conditionsSideblock_conditionAnd-text"])}>
							{CONDITION_CONFIRMATION_LIST_VARIANTS.find((el) => el.value === conditionAnd.value)?.translation === "title_confirmation" ? (
								<span>{translate("title_confirmation")}</span>
							) : (
								<span>{translate("title_noConfirmation")}</span>
							)}
						</span>
						{lastElem && (
							<span
								className={cn(styles["conditionsSideblock_conditionAnd-text"])}
								style={{ minWidth: "30px" }}
								title={elementsRemained ? `...(+${elementsRemained})` : ""}
							>
								{elementsRemained ? `...(+${elementsRemained})` : ""}
							</span>
						)}
					</div>
				);
			case "interval":
				return (
					<div key={JSON.stringify(conditionAnd)} className={cn(styles["conditionsSideblock_conditionAnd"])}>
						<span className={cn(styles["conditionsSideblock_conditionAnd-icon"])}>{getConditionIcon(conditionAnd.type)}</span>
						{conditionAnd.id ? (
							<span className={cn(styles["conditionsSideblock_conditionAnd-text"])} title={conditionAnd.id ? conditionAnd.id : ""}>
								{conditionAnd.id}
							</span>
						) : (
							<></>
						)}
						{lastElem && (
							<span
								className={cn(styles["conditionsSideblock_conditionAnd-text"])}
								style={{ minWidth: "30px" }}
								title={elementsRemained ? `...(+${elementsRemained})` : ""}
							>
								{elementsRemained ? `...(+${elementsRemained})` : ""}
							</span>
						)}
					</div>
				);
			case "event":
				return (
					<div key={JSON.stringify(conditionAnd)} className={cn(styles["conditionsSideblock_conditionAnd"])}>
						<span className={cn(styles["conditionsSideblock_conditionAnd-icon"])}>{getConditionIcon(conditionAnd.type)}</span>
						<span className={cn(styles["conditionsSideblock_conditionAnd-text"])}>
							{translate(ROBOT_EVENT_LIST.find((el) => el.event === conditionAnd.id)?.translation ?? "")}
						</span>
						{lastElem && (
							<span
								className={cn(styles["conditionsSideblock_conditionAnd-text"])}
								style={{ minWidth: "30px" }}
								title={elementsRemained ? `...(+${elementsRemained})` : ""}
							>
								{elementsRemained ? `...(+${elementsRemained})` : ""}
							</span>
						)}
					</div>
				);

			case "break":
				// добавить рендер для break если понадобиться
				return null;

			default:
				return null;
		}
	};

	const renderConditionAnd = (conditionAnd: ConditionVariantType, lastElem: boolean, elementsRemained?: number) => {
		switch (conditionAnd.type) {
			case "or":
				return (
					<span style={{ display: "flex", flexDirection: "row", gap: "4px" }}>
						{conditionAnd.value.slice(0, 2).map((conditionOr, idx, array) => (
							<span key={idx} style={{ display: "flex", gap: "4px" }}>
								{renderAtomicConditions(conditionOr, lastElem, elementsRemained)}
								{idx < array.length - 1 && " || "}
							</span>
						))}

						{conditionAnd.value.length > 2 && ` || ...(${conditionAnd.value.length - 2})`}
					</span>
				);
			case "break":
				return;
			case "confirmation":
				return renderAtomicConditions(conditionAnd, lastElem, elementsRemained);
			case "data":
				return renderAtomicConditions(conditionAnd, lastElem, elementsRemained);
			case "event":
				return renderAtomicConditions(conditionAnd, lastElem, elementsRemained);
			case "interval":
				return renderAtomicConditions(conditionAnd, lastElem, elementsRemained);
			case "variable":
				return renderAtomicConditions(conditionAnd, lastElem, elementsRemained);
			default:
				return;
		}
	};

	return (
		<>
			<div className={cn(styles["conditionsSideblock"])} ref={containerForOrConditions} {...props}>
				{conditions.map((conditionOr, index, array) => {
					return index < conditionInContainer - 1 ? (
						<>
							<div
								key={index}
								className={cn(styles["conditionsSideblock_conditionOr"])}
								style={{
									width: conditions.length < conditionInContainer ? "100%" : `${100 / conditionInContainer - 0.5}%`,
									maxWidth: conditions.length > conditionInContainer ? `24%` : "none",
								}}
							>
								{conditionOr
									.slice(0, 2)
									.map((conditionAnd, index) =>
										index < 2 ? index < 1 ? renderConditionAnd(conditionAnd, false) : renderConditionAnd(conditionAnd, true, conditionOr.length - 2) : <></>
									)}
							</div>
							{index < array.length - 1 && index < conditionInContainer - 1 ? <Divider sx={{ borderWidth: "1px" }} /> : <></>}
						</>
					) : index === conditionInContainer - 1 ? (
						<div style={{ margin: "auto", minWidth: "33px", maxWidth: "37px" }}>{`...(+${array.length - conditionInContainer})`}</div>
					) : (
						<></>
					);
				})}
			</div>
		</>
	);
};

export default ConditionsSideblock;
